/**
 * FieldCard — single field with provenance, confidence, validation, and inline edit.
 * From FRONTEND_BUILD_SPEC §3.5 and UI_UX_SPEC §3.6
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, FileText, AlertTriangle, Upload } from "lucide-react";
import { ProvenanceBadge } from "../shared/ProvenanceBadge.tsx";
import { Tooltip } from "../shared/Tooltip.tsx";
import { useToast } from "../shared/Toast.tsx";
import { api } from "../../lib/api.ts";

interface FieldCardProps {
  field: {
    field_id: string;
    display_label: string;
    category: string;
    data_type: string;
    value: string | null;
    unit: string | null;
    source_method: string | null;
    confidence: number | null;
    manual_override: boolean;
    source_document_id: string | null;
    source_page: number | null;
    source_doc_name: string | null;
    source_file_name: string | null;
    guidance_note: string | null;
    validation_min: number | null;
    validation_max: number | null;
    options: string | null;
    format_hint: string | null;
    source_doc_type: string | null;
    regulator: string | null;
  };
  projectId: string;
  required?: boolean;
  onUpdate?: (fieldId: string, newValue: string | null) => void;
  /** True if the field's source_doc_type is mandatory for at least one license submission.
   *  When true, the user MUST upload that doc anyway (typing the field value isn't enough). */
  docIsRequired?: boolean;
}

export function FieldCard({ field, projectId, required = false, onUpdate, docIsRequired = false }: FieldCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.value || "");
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { showToast } = useToast();

  // Keep local editValue in sync when field.value changes externally (e.g., parent re-fetches)
  useEffect(() => {
    setEditValue(field.value || "");
    setEditing(false);
  }, [field.value]);

  const hasValue = field.value != null && field.value !== "";
  const provenance = hasValue ? (field.source_method || "manual") : "empty";
  const options: string[] = field.options ? tryParseJSON(field.options, []) : [];

  function validate(val: string): string | null {
    if (!val) return null;
    if (field.data_type === "number" || field.data_type === "currency") {
      const num = parseFloat(val);
      if (isNaN(num)) return "Must be a number";
      if (field.validation_min != null && num < field.validation_min)
        return `Minimum: ${field.validation_min}${field.unit ? ` ${field.unit}` : ""}`;
      if (field.validation_max != null && num > field.validation_max)
        return `Maximum: ${field.validation_max}${field.unit ? ` ${field.unit}` : ""}`;
    }
    return null;
  }

  async function saveField() {
    const err = validate(editValue);
    if (err) {
      setValidationError(err);
      return;
    }
    setSaving(true);
    try {
      await api.updateProjectField(projectId, field.field_id, editValue || null);
      onUpdate?.(field.field_id, editValue || null);
      setEditing(false);
      setValidationError(null);
      showToast(`${field.display_label} saved`, "success");
    } catch (e: any) {
      showToast(e?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  }

  function startEditing() {
    setEditValue(field.value || "");
    setValidationError(null);
    setEditing(true);
  }

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${
      !hasValue && required ? "border-danger/30 bg-danger-light/30" : "border-border bg-surface"
    }`}>
      {/* Header row — label + provenance + expand */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          provenance === "extracted" ? "bg-info" :
          provenance === "calculated" ? "bg-ai" :
          provenance === "needs_review" ? "bg-warning" :
          provenance === "manual" ? "bg-text-tertiary" :
          /* empty */ "bg-danger"
        }`} />
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 min-w-0 text-left flex items-center gap-1.5"
        >
          <span className="text-[16px] font-medium">{field.display_label}</span>
          <Tooltip text="Field identifier"><span className="font-mono text-[11px] text-text-tertiary flex-shrink-0">{field.field_id}</span></Tooltip>
        </button>
        <ProvenanceBadge sourceMethod={provenance} confidence={field.confidence} />
        <button onClick={() => setExpanded(!expanded)} className="text-text-tertiary flex-shrink-0">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Value row */}
      <div className="px-3 pb-2.5 pl-[26px]">
        {editing ? (
          <InlineEditor
            field={field}
            editValue={editValue}
            setEditValue={setEditValue}
            validationError={validationError}
            setValidationError={setValidationError}
            options={options}
            saving={saving}
            onSave={saveField}
            onCancel={() => setEditing(false)}
          />
        ) : hasValue ? (
          <button onClick={startEditing} className="text-left">
            <span className="text-[14px] text-text-secondary break-words">
              {field.value}
              {field.unit && <span className="text-text-tertiary ml-1">{field.unit}</span>}
            </span>
          </button>
        ) : field.source_doc_type && docIsRequired ? (
          /* Document is mandatory for submission — push the user to upload it */
          <div>
            {field.guidance_note && !expanded && (
              <p className="text-[12px] text-text-tertiary mb-1.5 leading-snug">{field.guidance_note}</p>
            )}
            <Link
              to={`/projects/${projectId}?tab=documents`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-warning-light text-warning rounded-lg text-[13px] font-medium no-underline hover:bg-warning/10 transition-colors"
            >
              <Upload size={12} /> Upload {field.source_doc_type.replace(/_/g, " ")} →
            </Link>
            <p className="text-[11px] text-text-tertiary mt-1">
              The regulator requires this document. Uploading also auto-fills this field.
            </p>
          </div>
        ) : (
          /* Manual-entry field (no required doc, or doc is just a convenience source) */
          <div>
            {field.guidance_note && !expanded && (
              <p className="text-[12px] text-text-tertiary mb-1.5 leading-snug">{field.guidance_note}</p>
            )}
            <InlineEditor
              field={field}
              editValue={editValue}
              setEditValue={(v) => { setEditValue(v); setValidationError(null); }}
              validationError={validationError}
              setValidationError={setValidationError}
              options={options}
              saving={saving}
              onSave={saveField}
              onCancel={null}
            />
            {field.source_doc_type && (
              <Link
                to={`/projects/${projectId}?tab=documents`}
                className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-text-tertiary hover:text-info no-underline"
              >
                <Upload size={10} /> Or upload {field.source_doc_type.replace(/_/g, " ")} to auto-fill
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="px-3 pb-2 flex items-center gap-1.5 text-[13px] text-danger">
          <AlertTriangle size={12} />
          {validationError}
          {field.validation_min != null && field.validation_max != null && (
            <span className="text-text-tertiary ml-1">
              (Valid range: {field.validation_min}–{field.validation_max}{field.unit ? ` ${field.unit}` : ""})
            </span>
          )}
        </div>
      )}

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border/50 space-y-2 ml-[14px]">
          {/* Regulator */}
          {field.regulator && (
            <div className="text-[13px] text-text-tertiary">
              Regulator: <span className="text-text-secondary">{field.regulator}</span>
            </div>
          )}

          {/* Validation range — only show if at least one bound is meaningful (non-zero) */}
          {((field.validation_min != null && field.validation_min !== 0) || field.validation_max != null) && (
            <div className="text-[13px] text-text-tertiary">
              Valid range: {field.validation_min ?? "—"} – {field.validation_max ?? "—"}{field.unit ? ` ${field.unit}` : ""}
            </div>
          )}

          {/* Manual override warning */}
          {field.manual_override && (
            <div className="flex items-center gap-1.5 text-[13px] text-warning">
              <AlertTriangle size={12} />
              <span>Manual override — extraction will not overwrite this value</span>
            </div>
          )}

          {/* Source doc type hint (when no value yet) */}
          {field.source_doc_type && !hasValue && (
            <div className="text-[13px] text-text-tertiary">
              Extractable from: <span className="text-info">{field.source_doc_type.replace(/_/g, " ")}</span>
            </div>
          )}

          {/* Extraction source — single card with doc name, page, confidence, thumbnail */}
          {field.source_document_id && field.source_method === "extracted" && (
            <div className="bg-info-light/50 border border-info/10 rounded-lg overflow-hidden">
              <div className="flex items-start gap-2 text-[13px] px-2.5 py-2">
                <FileText size={14} className="text-info flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-text-secondary">
                    Extracted from <span className="font-medium text-text-primary">{field.source_doc_name || field.source_file_name || "document"}</span>
                    {field.source_page != null && field.source_page > 0 && <span className="text-text-tertiary"> · page {field.source_page}</span>}
                    {field.confidence != null && field.confidence > 0 && <span className="text-text-tertiary"> · {Math.round(field.confidence * 100)}% confidence</span>}
                  </span>
                </div>
              </div>
              {field.source_page != null && field.source_page > 0 && (
                <div className="px-2.5 pb-2">
                  <img
                    src={`/api/thumbnails/${field.source_document_id}/${field.source_page}`}
                    alt={`Source: ${field.source_doc_name || "document"} page ${field.source_page}`}
                    className="w-full max-h-48 object-contain object-top rounded border border-border bg-white"
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Guidance note */}
          {field.guidance_note && (
            <div className="text-[13px] text-text-secondary leading-relaxed bg-surface-secondary rounded px-2 py-1.5">
              {field.guidance_note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline Editor (reused for both edit-mode and always-visible empty fields) ───

function InlineEditor({ field, editValue, setEditValue, validationError, setValidationError, options, saving, onSave, onCancel }: {
  field: { data_type: string; unit: string | null; format_hint: string | null; guidance_note: string | null };
  editValue: string;
  setEditValue: (v: string) => void;
  validationError: string | null;
  setValidationError: (v: string | null) => void;
  options: string[];
  saving: boolean;
  onSave: () => void;
  onCancel: (() => void) | null;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {field.data_type === "categorical" && options.length > 0 ? (
        <select
          value={editValue}
          onChange={e => { setEditValue(e.target.value); setValidationError(null); }}
          className="w-48 px-2 py-1 border border-border rounded text-[14px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30"
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.data_type === "number" || field.data_type === "currency" ? "number" : field.data_type === "date" ? "date" : "text"}
          value={editValue}
          onChange={e => { setEditValue(e.target.value); setValidationError(null); }}
          placeholder={field.format_hint || "Enter value..."}
          className={`flex-1 min-w-0 px-2 py-1 border rounded text-[14px] focus:outline-none focus:ring-1 ${
            validationError ? "border-danger focus:ring-danger/30" : "border-border focus:ring-brand/30"
          }`}
          onKeyDown={e => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape" && onCancel) onCancel();
          }}
        />
      )}
      {field.unit && <span className="text-[13px] text-text-tertiary">{field.unit}</span>}
      <button
        onClick={onSave}
        disabled={saving || !editValue.trim()}
        className="px-2 py-0.5 bg-brand text-white rounded text-[13px] font-medium disabled:opacity-50 flex-shrink-0"
      >
        {saving ? "..." : "Save"}
      </button>
      {onCancel && (
        <button
          onClick={onCancel}
          className="px-2 py-0.5 text-text-secondary text-[13px] flex-shrink-0"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

import { tryParseJSON } from "../../lib/utils.ts";
